// Dashboard Pain widget (PAIN_TRACKING_PLAN.md Task 5.4).
//
// Up to three active pain sites, each with a mini read-only body-map canvas,
// last severity, and a one-tap Same / Better / Worse / Gone check-in row that
// posts to /symptom-follow-up with the site's `siteId` (D-PAIN-4 — the server
// copies the bodyMap forward so the site does not fork). Self-hides when there
// are no active sites, like OpenSymptomsWidget.
//
// H3 (no linter): every hook sits at the top of the component body.

import { useState } from "react";
import { CrosshairIcon } from "../../icons.js";
import { relativeTime } from "../../../lib/time.js";
import { ApiError, recordSymptomFollowUp } from "../../../lib/api.js";
import { PainMapCanvas } from "../../bodymap/PainMapCanvas.js";
import { groupPainSites, type PainSite } from "../../pain/painSites.js";
import type { WidgetProps } from "../types.js";

const DELTAS = [
  { label: "Same", delta: 0 },
  { label: "Better", delta: -2 },
  { label: "Worse", delta: 2 },
] as const;

export function PainWidget({ patient, events, onNavigate, onError, onReload }: WidgetProps) {
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const sites = groupPainSites(events).slice(0, 3);
  if (sites.length === 0) return null;

  async function checkIn(site: PainSite, delta: number, resolved: boolean) {
    setBusyKey(site.key);
    onError(null);
    try {
      await recordSymptomFollowUp({
        patientId: patient.patientId,
        key: site.key,
        eventType: site.eventType,
        severity: resolved ? 0 : Math.min(10, Math.max(1, site.latestSeverity + delta)),
        resolved: resolved || undefined,
        siteId: site.siteId,
      });
      onReload();
    } catch (e) {
      onError(e instanceof ApiError ? e.message : "Check-in failed");
    } finally {
      setBusyKey(null);
    }
  }

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
      <div className="mb-2 flex items-center gap-2">
        <CrosshairIcon size={16} className="text-(--accent)" />
        <h2 className="font-semibold text-gray-900 dark:text-gray-100">Pain</h2>
        <button onClick={() => onNavigate("pain")} className="ml-auto text-xs text-(--accent) hover:underline">
          View all
        </button>
      </div>
      <ul className="space-y-3">
        {sites.map((site) => {
          const wview = site.marks.some((m) => m.view === "front") ? "front" : site.primaryView;
          const name = (site.label || site.name).replace(/^\w/, (c) => c.toUpperCase());
          const busy = busyKey === site.key;
          return (
            <li key={site.key} className="flex gap-3">
              <div className="w-12 shrink-0">
                {site.marks.length > 0 && (
                  <PainMapCanvas view={wview} marks={site.marks} artworkVersion={site.artworkVersion} readOnly maxHeightPx={72} />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-gray-800 dark:text-gray-100">{name}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {site.latestSeverity}/10 · {relativeTime(site.lastUpdatedAt)}
                </p>
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {DELTAS.map((d) => (
                    <button
                      key={d.label}
                      disabled={busy}
                      onClick={() => void checkIn(site, d.delta, false)}
                      className="rounded-full border border-gray-300 px-2 py-0.5 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                    >
                      {d.label}
                    </button>
                  ))}
                  <button
                    disabled={busy}
                    onClick={() => void checkIn(site, 0, true)}
                    className="rounded-full border border-rose-300 px-2 py-0.5 text-xs font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50 dark:border-rose-700 dark:text-rose-300 dark:hover:bg-rose-950"
                  >
                    Gone
                  </button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
